import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import type { InstalledAppVersion } from '@h2h/shared';

export interface AppVersionInfo extends InstalledAppVersion {
  /** True only on the installed native app, where an update means the App Store. */
  native: boolean;
}

/**
 * The installed app's own version. On native (iOS) it's the real
 * `CFBundleShortVersionString` / `CFBundleVersion` via `@capacitor/app`. On the
 * web there's no "installed build" to update — a refresh always ships the latest
 * deploy — so we report `native: false` and the force-update gate is a no-op
 * there (which also means the web admin dashboard can never lock itself out).
 *
 * The result never changes during a session, so it's resolved once and cached.
 * Any failure to read the native version fails OPEN (native: false) so a plugin
 * hiccup can't block the app.
 */
let cached: Promise<AppVersionInfo> | null = null;

export function getAppVersion(): Promise<AppVersionInfo> {
  if (!cached) {
    cached = (async (): Promise<AppVersionInfo> => {
      if (!Capacitor.isNativePlatform()) {
        return { version: '0.0.0', build: 0, native: false };
      }
      try {
        const info = await App.getInfo();
        return {
          version: info.version || '0.0.0',
          build: Number(info.build) || 0,
          native: true
        };
      } catch {
        return { version: '0.0.0', build: 0, native: false };
      }
    })();
  }
  return cached;
}
